import { X } from "lucide-react";

import { createColor } from "./createColor";
import styles from "./Tag.module.css";

/**
 * Labels longer than this get the CSS clip and a tooltip with the full value.
 * Above the stylesheet's 20ch cutoff on purpose — `ch` is the width of "0" and
 * tag glyphs are narrower, so text keeps fitting past 20 (measured: overflow
 * starts at 23-25). Gating lower would tooltip labels already fully visible.
 */
export const TAG_MAX_LENGTH = 25;

export interface TagProps {
  label: string;
  /**
   * Optional leading-dot color (any CSS color string).
   */
  color?: string;
  /**
   * Optional onClick callback. When set, the tag body (dot + label) renders as
   * a real button so it is keyboard- and screen-reader-accessible.
   */
  onClick?: () => void;
  /**
   * Optional onDelete callback. If set, renders a delete button.
   */
  onDelete?: () => void;
  /**
   * Accessible name for the delete button, when "Remove <label>" would
   * misdescribe it. A tag staged for deletion is the case that needs this:
   * its button *cancels* the deletion, so the default name says the opposite
   * of what pressing it does.
   */
  deleteLabel?: string;
}

export function Tag(props: TagProps) {
  const {
    label,
    onClick,
    onDelete,
    deleteLabel,
    color = createColor(props.label),
  } = props;

  const isTruncated = label.length > TAG_MAX_LENGTH;

  const content = (
    <>
      <span className={styles.dot} style={{ backgroundColor: color }} />
      <span
        className={isTruncated ? styles.labelTruncated : styles.label}
        title={isTruncated ? label : undefined}
      >
        {label}
      </span>
    </>
  );

  return (
    <span className={styles.tag}>
      {onClick ? (
        <button type="button" className={styles.body} onClick={onClick}>
          {content}
        </button>
      ) : (
        content
      )}
      {onDelete && (
        <button
          type="button"
          className={styles.deleteButton}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={deleteLabel ?? `Remove ${label}`}
        >
          <X className={styles.icon} />
        </button>
      )}
    </span>
  );
}
